/**
 * The pure shaping behind the daily traffic chart: which platforms get a band,
 * the colours, one reading per day, and the axis it is drawn against. Kept out
 * of the component so the arithmetic is testable and the chart file is only
 * drawing.
 *
 * Everything here is a function of the gateway's live daily series. The chart
 * used to read a hand-copied SQL dump, which froze the whole plot at the day it
 * was taken; there is no fixture left to go stale.
 */

import type { NetworkDay } from '../../../data/analytics';
import { fmtReq, usdCost } from '../../../lib/format';
import { platformMeta } from '../../../lib/catalogPlatform';
import { brandHues } from './brandHues';

/** What the chart is measuring: calls served, or the USD they were charged.
 *  Both readings come off the same days and the same platforms. */
export type TrafficUnit = 'requests' | 'spend';

/** One platform's slice of a single day. */
interface DayPart {
  id: string;
  value: number;
}

/** Everything the chart and its tooltip need about one day. */
export interface DayReading {
  date: string;
  total: number;
  /** platforms that served traffic that day, in the window's band order */
  parts: DayPart[];
  /** that day's uptime across every platform; absent when no request that
   *  day was measured */
  uptimePct?: number;
}

/** The whole plot, derived once from the live series. */
export interface DailyTraffic {
  /** what every value in here is counted in */
  unit: TrafficUnit;
  /** one reading per day, oldest first */
  readings: DayReading[];
  /** window totals in band order: busiest platform first, the leftover last */
  totals: DayPart[];
  /** the brand colour each band is drawn in, keyed by the same ids */
  hues: Record<string, string>;
  /** gridline spacing, how many rungs, and the value the plot is scaled to */
  step: number;
  lines: number;
  ceiling: number;
}

/** The id every platform outside the named bands is folded into. */
export const OTHER_ID = 'other';

/** How many platforms get a band of their own; the rest fold into the
 *  leftover. Seven is the count the chart was designed and reviewed at. */
const BANDS = 7;

/** The leftover band is drawn in the page's own grey rather than a colour it
 *  would then share with a platform. */
export const OTHER_HUE = 'color-mix(in oklab, var(--c-slate) 40%, transparent)';

/** What a band is called on screen. Platform names come from the catalog's one
 *  owner, so the chart and every card on the site say X / Twitter the same way. */
export const labelFor = (id: string): string =>
  id === OTHER_ID ? 'Every other platform' : platformMeta(id).name;

/** One day's platform split in the unit being drawn. A gateway that has not
 *  published spend yet reads as a day that settled nothing, which is the same
 *  empty plot an unmeasured window already renders. */
const splitOf = (day: NetworkDay, unit: TrafficUnit): Record<string, number> =>
  (unit === 'spend' ? day.spend : day.platforms) ?? {};

/** One day's total in the unit being drawn. */
const totalOf = (day: NetworkDay, unit: TrafficUnit): number =>
  (unit === 'spend' ? day.spendUsd : day.requests) ?? 0;

/** The value a reader sees, in the unit it was measured in. The chart, its
 *  tooltip, its legend and its text alternative all read through this one
 *  function, so they cannot disagree about what a number means. */
export const fmtTraffic = (unit: TrafficUnit, value: number): string =>
  unit === 'spend' ? usdCost(value) : fmtReq(value);

/** Window totals per platform id, largest first. */
const windowTotals = (days: NetworkDay[], unit: TrafficUnit): DayPart[] => {
  const totals = new Map<string, number>();
  for (const day of days) {
    for (const [id, value] of Object.entries(splitOf(day, unit))) {
      totals.set(id, (totals.get(id) ?? 0) + value);
    }
  }
  return [...totals]
    .map(([id, value]) => ({ id, value }))
    .filter((part) => part.value > 0)
    .sort((a, b) => b.value - a.value || a.id.localeCompare(b.id));
};

/** The smallest 1, 2 or 5 times a power of ten at or above `n`, so the grid
 *  lands on readable numbers without stranding the plot in headroom: rounding
 *  to a flat 5,000 put a 23,784 peak on a 40,000 axis and threw away two fifths
 *  of the height. */
const niceStep = (n: number): number => {
  const magnitude = 10 ** Math.floor(Math.log10(n));
  const scaled = n / magnitude;
  const rounded = scaled <= 1 ? 1 : scaled <= 2 ? 2 : scaled <= 5 ? 5 : 10;
  return rounded * magnitude;
};

/** An empty plot, for a window the gateway has not answered for yet. */
const NOTHING: Omit<DailyTraffic, 'unit'> = {
  readings: [],
  totals: [],
  hues: {},
  step: 1,
  lines: 1,
  ceiling: 1,
};

/**
 * The plot, from the gateway's zero-filled daily series.
 *
 * The busiest platforms across the WHOLE window take the named bands and every
 * other platform is folded into one leftover band, so a band sits at the same
 * height in every column and the stack reads as bands instead of shuffling
 * colour every day. Ranking each day on its own would do the opposite.
 *
 * The ranking is taken in the unit being drawn, so the dollars plot names the
 * platforms that earned the most rather than the ones that were called the
 * most. A platform can therefore hold a different band, and a different colour,
 * in each of the two readings; the legend beside the plot is what says which.
 */
export const buildDailyTraffic = (
  days: NetworkDay[],
  unit: TrafficUnit = 'requests',
): DailyTraffic => {
  const ranked = windowTotals(days, unit);
  if (days.length === 0 || ranked.length === 0) return { ...NOTHING, unit };

  const named = ranked.slice(0, BANDS);
  const band = new Map(named.map((part, i) => [part.id, i]));
  const leftover = ranked.slice(BANDS).reduce((n, part) => n + part.value, 0);

  const totals = leftover > 0 ? [...named, { id: OTHER_ID, value: leftover }] : named;
  const hues: Record<string, string> = {
    ...brandHues(named.map((part) => part.id)),
    [OTHER_ID]: OTHER_HUE,
  };

  const readings = days.map((day) => {
    // One slot per band plus one for the leftover, so a platform that ranked
    // outside the named set always has somewhere to land.
    const values = new Array<number>(named.length + 1).fill(0);
    for (const [id, value] of Object.entries(splitOf(day, unit))) {
      values[band.get(id) ?? named.length] += value;
    }
    const parts = totals
      .map((part, i) => ({ id: part.id, value: values[i] ?? 0 }))
      .filter((part) => part.value > 0);
    const measured = (day.uptimeSample ?? 0) > 0 && day.uptimePct !== undefined;
    return {
      date: day.date,
      total: totalOf(day, unit),
      parts,
      ...(measured ? { uptimePct: day.uptimePct } : {}),
    };
  });

  const peak = Math.max(1, ...readings.map((reading) => reading.total));
  const step = niceStep(peak / 5);
  const lines = Math.max(1, Math.ceil(peak / step));
  return { unit, readings, totals, hues, step, lines, ceiling: step * lines };
};

/** Short month name on the first of a month, for the axis beneath the columns. */
export const monthLabel = (iso: string): string | null =>
  iso.endsWith('-01')
    ? new Date(`${iso}T00:00:00Z`).toLocaleString('en-US', { month: 'short', timeZone: 'UTC' })
    : null;
